"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Backdrop from "components/Backdrop"
import ErrorBox from "components/ErrorBox"
import IconButton from "components/IconButton"
import { toast } from "react-toastify"
import { useIsManager, usePermissions } from "app/hooks"
import { getAllEmployeeUtilizationDetail, approveEmployeeUtilization, bulkApproveUtilizations, getAllManagers} from "api/apiHelper"
import type { EmployeeUtilization, BulkApprovalRequest, UtilizationItem } from "types/employee-utilization"
import dayjs from "dayjs"
import { getFirstDayOfMonth, getLastDayOfMonth, getMonday, getSunday } from "utils/utilityFunctions";
import MatrixView from "components/MatrixView"
import ApprovalView from "components/ApprovalView"

interface UtilizationEntry {
  id: string
  employee_name: string
  date: string
  category: string
  hours: number
  status: "Pending" | "Manager Approved" | "Manager Declined"
  created_at: string
  items: UtilizationItem[]
}
interface Manager {
  id: number;
  first_name: string;
  last_name: string;
  designation?: string;
}
interface ManagersResponse {
  managers: Manager[];
  total_managers: number;
}


// Transform backend data to frontend format
const transformToUtilizationEntry = (record: EmployeeUtilization): UtilizationEntry => {
  return {
    id: record.id.toString(),
    employee_name: record.employee_name,
    date: record.date,
    category: record.items && record.items.length > 0 ? record.items[0].type_name || "Unknown" : "Unknown",
    hours: record.total_hours || 0,
    status:
      record.status === "pending" ? "Pending" : record.status === "approved" ? "Manager Approved" : "Manager Declined",
    created_at: record.created_at,
    items: record.items || [],
  }
}

const ManagerUtilizationsList = () => {
  // State management
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [utilizations, setUtilizations] = useState<EmployeeUtilization[]>([])
  const [utilizationEntries, setUtilizationEntries] = useState<UtilizationEntry[]>([])
  const [startDate, setStartDate] = useState<string>("")
  const [endDate, setEndDate] = useState<string>("")
  const [activeFilter, setActiveFilter] = useState<"week" | "month" | "custom">("week")
  const [viewMode, setViewMode] = useState<"matrix" | "approval">("matrix")
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "approved" | "declined">("all")
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set())
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const isManager = useIsManager();
  const [managers, setManagers] = useState<Manager[]>([]);
  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(null);
  const [selectedManagerName, setSelectedManagerName] = useState<string>("Default Manager");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isLoadingManagers, setIsLoadingManagers] = useState<boolean>(false);
  

  const navigate = useNavigate()
  const permissions = usePermissions() || []

  // Data fetching
  const fetchUtilizations = async (filters: { 
    start_date?: Date; 
    end_date?: Date; 
    manager_id?: number | null;
  }) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await getAllEmployeeUtilizationDetail(
        filters.start_date || null, 
        filters.end_date || null,
        filters.manager_id || null
      )
  
      if (response?.data) {
        const transformedEntries = response.data.map((record: EmployeeUtilization) =>
          transformToUtilizationEntry(record),
        )
        setUtilizations(response.data)
        setUtilizationEntries(transformedEntries)
      } else {
        setError("No data received from server")
      }
    } catch (err: any) {
      console.error("Fetch Error:", err)
      setError(err.response?.data?.detail || "Failed to fetch utilization data")
    } finally {
      setIsLoading(false)
    }
  }
  
  // Add this function to fetch managers
  const fetchManagers = async () => {
    setIsLoadingManagers(true);
    try {
      const response = await getAllManagers(); // Your API call
      if (response?.data) {
        setManagers(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch managers:', error);
    } finally {
      setIsLoadingManagers(false);
    }
  };

  // Load managers on component mount
  useEffect(() => {
    fetchManagers();
    console.log(managers)
  }, []);

  const handleManagerSelect = (manager: Manager | null) => {
    if (manager) {
      setSelectedManagerId(manager.id);
      setSelectedManagerName(`${manager.first_name} ${manager.last_name}`);
    } else {
      setSelectedManagerId(null);
      setSelectedManagerName("Default Manager");
    }
    setIsDropdownOpen(false);
    
    // Refetch data with new manager filter
    fetchUtilizations({
      start_date: new Date(startDate),
      end_date: new Date(endDate),
      manager_id: manager?.id || null,
    });
  };
  // Initialize with current week
  useEffect(() => {
    const monday = getMonday(new Date())
    const sunday = getSunday(monday)
    const start = dayjs(monday).format("YYYY-MM-DD")
    const end = dayjs(sunday).format("YYYY-MM-DD")
    setStartDate(start)
    setEndDate(end)
    fetchUtilizations({
      start_date: new Date(start),
      end_date: new Date(end),
    })
  }, [])

  // Filter handlers
  const handleFilter = () => {
    setActiveFilter("custom")
    fetchUtilizations({
      start_date: new Date(startDate),
      end_date: new Date(endDate),
      manager_id: selectedManagerId,
    })
  }

  const handleShowThisWeek = () => {
    const monday = getMonday(new Date())
    const sunday = getSunday(monday)
    const start = dayjs(monday).format("YYYY-MM-DD")
    const end = dayjs(sunday).format("YYYY-MM-DD")
    setStartDate(start)
    setEndDate(end)
    setActiveFilter("week")
    fetchUtilizations({
      start_date: new Date(start),
      end_date: new Date(end),
      manager_id: selectedManagerId,
    })
  }

  const handleShowThisMonth = () => {
    const today = new Date()
    const first = getFirstDayOfMonth(today)
    const last = getLastDayOfMonth(today)
    const start = dayjs(first).format("YYYY-MM-DD")
    const end = dayjs(last).format("YYYY-MM-DD")
    setStartDate(start)
    setEndDate(end)
    setActiveFilter("month")
    fetchUtilizations({
      start_date: first,
      end_date: last,
      manager_id: selectedManagerId,
    })
  }

  // Approval handlers
  const handleApproveEntry = async (entryId: string) => {
    setIsProcessing(true)
    try {
      await approveEmployeeUtilization(Number.parseInt(entryId), "approved")
      await fetchUtilizations({
        start_date: new Date(startDate),
        end_date: new Date(endDate),
        manager_id: selectedManagerId,
      })
      toast.success("Entry approved successfully")
    } catch (error) {
      toast.error("Failed to approve entry")
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDeclineEntry = async (entryId: string) => {
    setIsProcessing(true)
    try {
      await approveEmployeeUtilization(Number.parseInt(entryId), "declined")
      await fetchUtilizations({
        start_date: new Date(startDate),
        end_date: new Date(endDate),
        manager_id: selectedManagerId,
      })
      toast.success("Entry declined successfully")
    } catch (error) {
      toast.error("Failed to decline entry")
    } finally {
      setIsProcessing(false)
    }
  }

  const handleBulkApprove = async () => {
    if (selectedEntries.size === 0) {
      toast.warning("Please select entries to approve")
      return
    }

    setIsProcessing(true)
    try {
      const data: BulkApprovalRequest = {
        utilization_ids: Array.from(selectedEntries).map((id) => Number.parseInt(id)),
        action: "approved",
      }
      await bulkApproveUtilizations(data)
      await fetchUtilizations({
        start_date: new Date(startDate),
        end_date: new Date(endDate),
      })
      setSelectedEntries(new Set())
      toast.success(`${selectedEntries.size} entries approved successfully`)
    } catch (error) {
      toast.error("Failed to approve entries")
    } finally {
      setIsProcessing(false)
    }
  }

  const handleBulkDecline = async () => {
    if (selectedEntries.size === 0) {
      toast.warning("Please select entries to decline")
      return
    }

    setIsProcessing(true)
    try {
      const data: BulkApprovalRequest = {
        utilization_ids: Array.from(selectedEntries).map((id) => Number.parseInt(id)),
        action: "declined",
      }
      await bulkApproveUtilizations(data)
      await fetchUtilizations({
        start_date: new Date(startDate),
        end_date: new Date(endDate),
      })
      setSelectedEntries(new Set())
      toast.success(`${selectedEntries.size} entries declined successfully`)
    } catch (error) {
      toast.error("Failed to decline entries")
    } finally {
      setIsProcessing(false)
    }
  }

  // Selection handlers
  const handleSelectEntry = (entryId: string) => {
    const newSelected = new Set(selectedEntries)
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId)
    } else {
      newSelected.add(entryId)
    }
    setSelectedEntries(newSelected)
  }

  const handleSelectAll = () => {
    const filteredEntries = utilizationEntries.filter((entry) => {
      const entryDate = dayjs(entry.date).format("YYYY-MM-DD")
      const matchesDateRange = (!startDate || entryDate >= startDate) && (!endDate || entryDate <= endDate)
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "pending" && entry.status === "Pending") ||
        (statusFilter === "approved" && entry.status === "Manager Approved") ||
        (statusFilter === "declined" && entry.status === "Manager Declined")
      return matchesDateRange && matchesStatus
    })

    if (selectedEntries.size === filteredEntries.length) {
      setSelectedEntries(new Set())
    } else {
      setSelectedEntries(new Set(filteredEntries.map((entry) => entry.id)))
    }
  }

  // Statistics
  const getApprovedUtilizations = () => {
    return utilizations.filter((record) => {
      const recordDate = dayjs(record.date).format("YYYY-MM-DD")
      const isWithinRange = (!startDate || recordDate >= startDate) && (!endDate || recordDate <= endDate)
      return record.status === "approved" && isWithinRange
    })
  }

  const getTotalHours = () => {
    return getApprovedUtilizations().reduce((total, record) => total + (record.total_hours || 0), 0)
  }

  const getAverageHours = () => {
    const approved = getApprovedUtilizations()
    if (approved.length === 0) return "0.0"
    return (getTotalHours() / approved.length).toFixed(1)
  }

  const getPendingCount = () => {
    return utilizationEntries.filter((entry) => entry.status === "Pending").length
  }

  const getEmployeeCount = () => {
    return Array.from(new Set(getApprovedUtilizations().map((u) => u.employee_name))).length
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex md:flex-row md:justify-between items-center mb-4">

        <div className="flex flex-wrap justify-start md:justify-start gap-2">
          <button onClick={() => navigate("/employee-utilization/")} className="px-6 py-3 rounded-lg font-medium transition-all bg-brand-500 dark:bg-brand-700 text-white hover:bg-brand-700 dark:hover:bg-brand-900/20">
            <i className="fas fa-tasks mr-2"></i>
            Employee Utilization
          </button>
        </div>
        <div className="flex flex-wrap justify-end md:justify-end gap-2">
            {permissions && permissions.includes('employee_utilization.add_employeeutilization') &&
              <IconButton url="/employee-utilization/create/" name="Create Records" icon="fas fa-plus" extraStyles="mb-2 md:mb-0" />
            }
            {permissions && permissions.includes('employee_utilization.add_workcategory') &&
              <IconButton url="/employee-utilization/create-work-category/" name="Create Category" icon="fas fa-plus" extraStyles="mb-2 md:mb-0" />
            }
        </div>
      </div>
       
      {/* View Mode Toggle */}
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-50 dark:bg-brand-900/20 rounded-lg flex items-center justify-center">
              <i className="fas fa-eye text-brand-600 dark:text-brand-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white">View Mode</h3>
          </div>

          {getPendingCount() > 0 && (
            <div className="flex items-center gap-2 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">
              <i className="fas fa-clock"></i>
              <span>{getPendingCount()} pending approvals</span>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setViewMode("matrix")}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${
              viewMode === "matrix"
                ? "bg-brand-500 text-white"
                : "bg-gray-100 dark:bg-navy-700 text-gray-700 dark:text-gray-300 hover:bg-brand-50 dark:hover:bg-brand-900/20"
            }`}
          >
            <i className="fas fa-table mr-2"></i>
            Matrix View
          </button>
          {(isManager) && (
            <button
              type="button"
              onClick={() => setViewMode("approval")}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                viewMode === "approval"
                  ? "bg-brand-500 text-white"
                  : "bg-gray-100 dark:bg-navy-700 text-gray-700 dark:text-gray-300 hover:bg-brand-50 dark:hover:bg-brand-900/20"
              }`}
            >
              <i className="fas fa-check-circle mr-2"></i>
              Approval View
              {getPendingCount() > 0 && (
                <span className="ml-2 px-2 py-1 bg-yellow-500 text-white rounded-full text-xs">{getPendingCount()}</span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-50 dark:bg-brand-900/20 rounded-lg flex items-center justify-center">
              <i className="fas fa-calendar-alt text-brand-600 dark:text-brand-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Filters</h3>
          </div>
        </div>
        {viewMode === "matrix" && permissions && permissions.includes('employee_utilization.view_employeeutilization') && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Manager
            </label>
            <div className="relative w-full md:w-80">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                disabled={isLoadingManagers}
                className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-left flex items-center justify-between"
              >
                <span className="truncate">
                  {isLoadingManagers ? "Loading..." : selectedManagerName}
                </span>
                <i className={`fas fa-chevron-${isDropdownOpen ? 'up' : 'down'} text-gray-400`}></i>
              </button>
              <i className="fas fa-user-tie absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              
              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-navy-700 border border-gray-300 dark:border-navy-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {/* All Managers Option */}
                  <button
                    type="button"
                    onClick={() => handleManagerSelect(null)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-navy-600 transition-colors ${
                      selectedManagerId === null 
                        ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400' 
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <i className="fas fa-users"></i>
                      <span className="font-medium">Default Manager</span>
                    </div>
                  </button>
                  
                  {/* Individual Managers */}
                  {managers.map((manager) => (
                    <button
                      key={manager.id}
                      type="button"
                      onClick={() => handleManagerSelect(manager)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-navy-600 transition-colors ${
                        selectedManagerId === manager.id 
                          ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400' 
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <i className="fas fa-user-tie"></i>
                        <div>
                          <div className="font-medium">
                            {manager.first_name} {manager.last_name}
                          </div>
                          {manager.designation && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {manager.designation}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                  
                  {managers.length === 0 && !isLoadingManagers && (
                    <div className="px-4 py-3 text-gray-500 dark:text-gray-400 text-center">
                      No managers found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  />
                  <i className="fas fa-calendar absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">End Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  />
                  <i className="fas fa-calendar absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleFilter}
                  className="px-6 py-3 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium transition-all"
                >
                  <i className="fas fa-search mr-2"></i>
                  Apply Filter
                </button>
              </div>
            </div>
          </div>

          <div className="lg:w-80">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Quick Filters</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleShowThisWeek}
                className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${
                  activeFilter === "week"
                    ? "bg-brand-500 text-white"
                    : "bg-gray-100 dark:bg-navy-700 text-gray-700 dark:text-gray-300 hover:bg-brand-50 dark:hover:bg-brand-900/20"
                }`}
              >
                <i className="fas fa-calendar-week mr-1"></i>
                This Week
              </button>

              <button
                type="button"
                onClick={handleShowThisMonth}
                className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${
                  activeFilter === "month"
                    ? "bg-brand-500 text-white"
                    : "bg-gray-100 dark:bg-navy-700 text-gray-700 dark:text-gray-300 hover:bg-brand-50 dark:hover:bg-brand-900/20"
                }`}
              >
                <i className="fas fa-calendar-alt mr-1"></i>
                This Month
              </button>
            </div>
          </div>
        </div>

        {/* Status Filter for Approval View */}
        {viewMode === "approval" && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-navy-600">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status Filter</label>
            <div className="flex gap-3">
              {[
                { key: "all", label: "All", icon: "fas fa-list" },
                { key: "pending", label: "Pending", icon: "fas fa-clock" },
                { key: "approved", label: "Approved", icon: "fas fa-check" },
                { key: "declined", label: "Declined", icon: "fas fa-times" },
              ].map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setStatusFilter(filter.key as any)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                    statusFilter === filter.key
                      ? "bg-brand-500 text-white"
                      : "bg-gray-100 dark:bg-navy-700 text-gray-700 dark:text-gray-300 hover:bg-brand-50 dark:hover:bg-brand-900/20"
                  }`}
                >
                  <i className={`${filter.icon} mr-1`}></i>
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Total Hours</p>
              <p className="text-3xl font-bold text-navy-700 dark:text-white">{getTotalHours()}</p>
            </div>
            <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl flex items-center justify-center">
              <i className="fas fa-clock text-xl text-emerald-600 dark:text-emerald-400"></i>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Employees</p>
              <p className="text-3xl font-bold text-navy-700 dark:text-white">{getEmployeeCount()}</p>
            </div>
            <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/20 rounded-xl flex items-center justify-center">
              <i className="fas fa-users text-xl text-purple-600 dark:text-purple-400"></i>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Pending</p>
              <p className="text-3xl font-bold text-navy-700 dark:text-white">{getPendingCount()}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl flex items-center justify-center">
              <i className="fas fa-clock text-xl text-yellow-600 dark:text-yellow-400"></i>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Avg Hours</p>
              <p className="text-3xl font-bold text-navy-700 dark:text-white">{getAverageHours()}</p>
            </div>
            <div className="w-12 h-12 bg-orange-50 dark:bg-orange-900/20 rounded-xl flex items-center justify-center">
              <i className="fas fa-chart-line text-xl text-orange-600 dark:text-orange-400"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Content based on view mode */}
      {isLoading ? (
        <Backdrop show={isLoading} />
      ) : error ? (
        <ErrorBox errorMessage={error} />
      ) : viewMode === "matrix" ? (
        <MatrixView utilizations={utilizations} startDate={startDate} endDate={endDate} isLoading={isLoading} />
      ) : (
        <ApprovalView
          utilizationEntries={utilizationEntries}
          startDate={startDate}
          endDate={endDate}
          statusFilter={statusFilter}
          selectedEntries={selectedEntries}
          isProcessing={isProcessing}
          onSelectEntry={handleSelectEntry}
          onSelectAll={handleSelectAll}
          onApproveEntry={handleApproveEntry}
          onDeclineEntry={handleDeclineEntry}
          onBulkApprove={handleBulkApprove}
          onBulkDecline={handleBulkDecline}
          isLoading={isLoading}
        />
      )}
    </div>
  )
}

export default ManagerUtilizationsList
